import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import accountsApi from "../../services/accountsCommonService";
import AccountsConstants from "../../common/AccountsConstants";
import { formatDate, formatINR, monthStartInput, todayInput } from "./accountUser";
import LedgerCombo from "./LedgerCombo";
import PageShell from "./PageShell";
import useAccountMeta from "./useAccountMeta";
import "./accounts.css";

function moneyOrBlank(amount) {
  return Number(amount) ? formatINR(amount) : "";
}

function balanceText(balance) {
  if (!balance) return "";
  const amount = balance.amount !== undefined ? balance.amount : balance;
  const type = balance.type || balance.balanceType;
  if (!Number(amount)) return formatINR(0);
  return `${formatINR(amount)} ${type === "CR" ? "Cr" : "Dr"}`;
}

function TallyParticulars({ ledgerName, row }) {
  const own = `${ledgerName || "Ledger"} A/c`;
  const other = `${row.particulars || "—"} A/c`;
  const thisIsDebit = Number(row.debit) > 0;
  return (
    <div className="tally-entry">
      <div className="tally-entry-dr">
        <span>{thisIsDebit ? own : other}</span>
        <span>Dr</span>
      </div>
      <div className="tally-entry-to">To {thisIsDebit ? other : own}</div>
      {row.narration ? <div className="tally-entry-narr">{row.narration}</div> : null}
    </div>
  );
}

export default function LedgerStatement() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { meta, error: metaError, labelOf } = useAccountMeta();
  const [ledgers, setLedgers] = useState([]);
  const [form, setForm] = useState({ ledgerId: searchParams.get("ledgerId") || "", fromDate: monthStartInput(), toDate: todayInput() });
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState("");
  const [downloadOpen, setDownloadOpen] = useState(false);
  const downloadRef = useRef(null);
  const rangeError = form.fromDate && form.toDate && form.fromDate > form.toDate ? "To date must be on or after from date" : "";

  useEffect(() => {
    accountsApi.ledgers().then((result) => setLedgers(result?.data || [])).catch((err) => setError(err.message));
  }, []);

  useEffect(() => {
    if (!downloadOpen) return undefined;
    const close = (event) => {
      if (downloadRef.current && !downloadRef.current.contains(event.target)) setDownloadOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [downloadOpen]);

  const show = async (event) => {
    event.preventDefault();
    if (!form.ledgerId || rangeError) return;
    setError("");
    setLoading(true);
    try {
      const result = await accountsApi.statement(form);
      setData(result.data);
    } catch (err) {
      setData(null);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const download = async (format) => {
    if (!form.ledgerId || rangeError || downloading) return;
    setError("");
    setDownloadOpen(false);
    setDownloading(format);
    try {
      const file = await accountsApi.downloadReport({
        report: "ledger-statement",
        format,
        filters: { ledgerId: form.ledgerId, fromDate: form.fromDate, toDate: form.toDate },
      });
      const url = URL.createObjectURL(file.blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.fileName || `ledger-statement.${format === "pdf" ? "pdf" : "xlsx"}`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setError(err.message);
    } finally {
      setDownloading("");
    }
  };

  return (
    <PageShell title="Ledger Statement" subtitle="Balances are computed from posted vouchers">
      <form className="accounts-panel tally-filter" onSubmit={show}>
        <LedgerCombo label="Ledger" required groups={meta?.ledgerGroups || []} ledgers={ledgers} value={form.ledgerId}
          onChange={(id) => setForm((current) => ({ ...current, ledgerId: id }))} />
        <label className="tally-field">From date
          <input className="form-control" type="date" value={form.fromDate} onChange={(event) => setForm((current) => ({ ...current, fromDate: event.target.value }))} />
        </label>
        <label className="tally-field">To date
          <input className={`form-control${rangeError ? " is-invalid" : ""}`} type="date" value={form.toDate} onChange={(event) => setForm((current) => ({ ...current, toDate: event.target.value }))} />
          {rangeError && <span className="accounts-hint tally-error">{rangeError}</span>}
        </label>
        <button className="btn tally-btn" type="submit" disabled={!form.ledgerId || Boolean(rangeError) || loading}>{loading ? "Showing…" : "Show"}</button>
      </form>
      {(error || metaError) && <div className="accounts-error">{error || metaError}</div>}
      {data && (
        <div className="accounts-panel tally-sheet">
          <div className="tally-sheet-head">
            <div>
              <h3>{data.ledger?.name}</h3>
              <p className="accounts-hint">
                {labelOf("ledgerGroups", data.ledger?.group)}
                {` · ${data.fromDate ? formatDate(data.fromDate) : "Beginning"} to ${data.toDate ? formatDate(data.toDate) : "date"}`}
              </p>
            </div>
            <div className="tally-download" ref={downloadRef}>
              <button
                type="button"
                className="tally-download-btn"
                aria-label="Download statement"
                aria-expanded={downloadOpen}
                disabled={Boolean(downloading)}
                onClick={() => setDownloadOpen((open) => !open)}
              >
                <i className={`bi ${downloading ? "bi-arrow-repeat tally-spin" : "bi-download"}`} />
              </button>
              {downloadOpen && (
                <div className="tally-download-menu" role="menu">
                  <button type="button" role="menuitem" onClick={() => download("excel")}>
                    <i className="bi bi-file-earmark-excel" /> Excel
                  </button>
                  <button type="button" role="menuitem" onClick={() => download("pdf")}>
                    <i className="bi bi-file-earmark-pdf" /> PDF
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="accounts-table-wrap">
            <table className="accounts-table tally-statement">
              <thead>
                <tr>
                  <th>Date</th><th>Particulars</th><th>Type</th><th>Voucher No</th>
                  <th className="num">Debit</th><th className="num">Credit</th><th className="num">Balance</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td />
                  <td colSpan={5}><strong>Opening Balance</strong></td>
                  <td className="num"><strong>{balanceText(data.openingBalance)}</strong></td>
                </tr>
                {(data.rows || []).map((row) => (
                  <tr key={`${row.voucherId}-${row.date}`}>
                    <td>{formatDate(row.date)}</td>
                    <td><TallyParticulars ledgerName={data.ledger?.name} row={row} /></td>
                    <td>{labelOf("voucherTypes", row.voucherType)}</td>
                    <td>
                      <button
                        type="button"
                        className="accounts-voucher-link"
                        title="View voucher details"
                        onClick={() => navigate(`${AccountsConstants.VOUCHER_VIEW}?id=${row.voucherId}`)}
                      >
                        <span>{row.voucherNo}</span>
                        <i className="bi bi-eye" aria-hidden="true" />
                      </button>
                    </td>
                    <td className="num">{moneyOrBlank(row.debit)}</td>
                    <td className="num">{moneyOrBlank(row.credit)}</td>
                    <td className="num">{balanceText({ amount: row.balance, type: row.balanceType })}</td>
                  </tr>
                ))}
                {(data.rows || []).length === 0 && (
                  <tr><td colSpan={7}>No posted vouchers for this ledger in the period.</td></tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td />
                  <td colSpan={3}><strong>Period Total</strong></td>
                  <td className="num"><strong>{formatINR(data.totals?.debit ?? data.totalDebit)}</strong></td>
                  <td className="num"><strong>{formatINR(data.totals?.credit ?? data.totalCredit)}</strong></td>
                  <td />
                </tr>
                <tr>
                  <td />
                  <td colSpan={5}><strong>Closing Balance</strong></td>
                  <td className="num"><strong>{balanceText(data.closingBalance)}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </PageShell>
  );
}
